import React from 'react';
import { Typography } from 'antd';
import { FormattedMessage } from 'react-intl';
import isUndefined from 'lodash/isUndefined';
import { HotelImagesSlider } from '../hotel-image-slider';
import { IImage, ITrustYouReview } from 'src/common-types/index';
import { formatRate, renderNumber } from 'src/utils/index';
import { FullScreenSvg, ImagesQuantitySvg, TrustYouSVG } from 'src/assets/index';
import './style.scss';

interface IProps {
  images: IImage[];
  hotelName: string;
  hotelDescription: string[];
  additionalDescriptions: string[];
  hotelRooms: number;
  onClickScrollToRooms?: (e: React.MouseEvent<HTMLDivElement>) => void;
  reviewModule: ITrustYouReview;
  onClickScrollToReviews?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

interface IState {
  isModalVisible: boolean;
  startIndex: number;
  isEllipsis: boolean;
  showMoreLink: boolean;
}

const FIRST_ELEMENT_ARRAY = 0;
const MIN_IMAGE = 0;
const MAX_IMAGE = 4;
const ONE_SECOND = 1000;

const { Paragraph } = Typography;

export class HotelImagesDescription extends React.Component<IProps, IState> {
  state: IState = {
    isModalVisible: false,
    startIndex: 0,
    isEllipsis: true,
    showMoreLink: false,
  };

  showSlider = (index: number): void => {
    this.setState({ isModalVisible: true });
    this.setState({ startIndex: index });
  };

  handleCancel = (e: React.MouseEvent<HTMLDivElement>, isScrollToRooms = false): void => {
    if (isScrollToRooms) {
      this.props.onClickScrollToRooms(e);
    }

    this.setState({ isModalVisible: false });
  };

  showMore = (): void => {
    this.setState({ isEllipsis: !this.state.isEllipsis });
  };

  componentDidMount(): void {
    setTimeout(() => {
      const moreLink: HTMLLinkElement = document.querySelector(
        '.hotel-images__hotel-text .ant-typography-expand',
      );

      if (moreLink) {
        this.setState({ showMoreLink: true });
      }
    }, ONE_SECOND);
  }

  getOtherImages = (images: IImage[]): React.ReactNode =>
    images.map((img: IImage, i: React.Key) => {
      if (i === MIN_IMAGE || i > MAX_IMAGE) {
        return;
      }
      return (
        <div key={i} className="hotel-images__other-image-wrapper">
          <img className="hotel-images__image" src={img.url} alt={img.description} />
          <div className="hotel-images__middle">
            <div
              className="hotel-images__btn-hover-wrapper"
              onClick={() => this.showSlider(Number(i))}
            >
              <FullScreenSvg />
              <span className="hotel-images__btn-hover-text">
                <FormattedMessage id="full.view" />
              </span>
            </div>
          </div>
        </div>
      );
    });

  render(): React.ReactNode {
    const {
      images = [],
      hotelName,
      hotelDescription = [],
      additionalDescriptions = [],
      hotelRooms,
      reviewModule,
      onClickScrollToReviews,
    } = this.props;
    const { isModalVisible, startIndex, isEllipsis, showMoreLink } = this.state;

    return (
      <div className="hotel-images">
        <div className="hotel-images__images-quantity">
          <ImagesQuantitySvg />
          <span className="hotel-images__images-quantity-text">{images.length}+</span>
        </div>
        <div className="hotel-images__wrapper">
          <div className="hotel-images__main-image-wrapper">
            <img
              className="hotel-images__image"
              src={images[FIRST_ELEMENT_ARRAY].url}
              width={images[FIRST_ELEMENT_ARRAY].width}
              alt={images[FIRST_ELEMENT_ARRAY].description}
            />
            <div className="hotel-images__middle">
              <div
                className="hotel-images__btn-hover-wrapper"
                onClick={() => this.showSlider(FIRST_ELEMENT_ARRAY)}
              >
                <FullScreenSvg />
                <span className="hotel-images__btn-hover-text">
                  <FormattedMessage id="full.view" />
                </span>
              </div>
            </div>
          </div>
          <div className="hotel-images__other-images-wrapper">{this.getOtherImages(images)}</div>
        </div>
        <HotelImagesSlider
          images={images}
          onCancel={this.handleCancel}
          visible={isModalVisible}
          hotelName={hotelName}
          startIndex={startIndex}
        />
        <div className="hotel-images__hotel-info">
          <div className="hotel-images__hotel-description">
            {!!hotelDescription.length && (
              <div className="hotel-images__hotel-text">
                <Paragraph ellipsis={isEllipsis ? { rows: 2, expandable: true } : false}>
                  {hotelDescription}
                  {!!additionalDescriptions.length && (
                    <div className="hotel-images__hotel-additional-descriptions">
                      {additionalDescriptions}
                    </div>
                  )}
                </Paragraph>

                {showMoreLink && (
                  <p className="hotel-images__read-more-button" onClick={this.showMore}>
                    {isEllipsis ? (
                      <FormattedMessage id="show.more" />
                    ) : (
                      <FormattedMessage id="show.less" />
                    )}
                  </p>
                )}
              </div>
            )}
            {!!additionalDescriptions.length && (
              <div className="hotel-images__hotel-text">
                <Paragraph ellipsis={isEllipsis ? { rows: 2, expandable: true } : false}>
                  {additionalDescriptions}
                </Paragraph>

                {showMoreLink && (
                  <p className="hotel-images__read-more-button" onClick={this.showMore}>
                    {isEllipsis ? (
                      <FormattedMessage id="show.more" />
                    ) : (
                      <FormattedMessage id="show.less" />
                    )}
                  </p>
                )}
              </div>
            )}
            {!hotelDescription.length &&
            !additionalDescriptions.length &&
            !isUndefined(reviewModule) &&
            reviewModule?.summary ? (
              <div className="hotel-details-wrapper__trust-you-info-wrapper padding-info">
                <div className="hotel-details-wrapper__trust-you-info">
                  <div className="hotel-details-wrapper__trust-you-icon">
                    <TrustYouSVG />
                  </div>
                  <p className="hotel-details-wrapper__trust-you-text">
                    <span className="hotel-details-wrapper__bold">
                      {reviewModule && formatRate(reviewModule.summary?.score)}
                    </span>
                    (
                    {reviewModule && reviewModule.reviews_count
                      ? renderNumber(reviewModule.reviews_count)
                      : null}{' '}
                    <FormattedMessage id="reviews" />)
                  </p>
                </div>
                {reviewModule ? (
                  <span
                    className="hotel-details-wrapper__link-text"
                    onClick={onClickScrollToReviews}
                  >
                    <FormattedMessage id="view.all" />
                  </span>
                ) : null}
              </div>
            ) : null}
          </div>
          {hotelRooms && (
            <div className="hotel-images__hotel-info-rooms">
              <p className="hotel-images__hotel-room-title">
                <FormattedMessage id="number.of.rooms" />:
              </p>
              <span className="hotel-images__rooms-quantity">{hotelRooms}</span>
            </div>
          )}
        </div>
      </div>
    );
  }
}
